/* global VueRouter */
import { ref, computed, watch } from "vue"

// The notification store. The `<Notification>` component that renders this state lives in
// components/Notification.vue - it is imported by App.vue only, while the setters below are used
// all over the app, so they stay in this plain module.
const _state = ref({
  success: {},
  errors: [],
  warnings: [],
  loading: false,
})

const _processes = new Set()

const useNotification = () => {
  return {
    state: {
      success: computed(() => _state.value.success),
      errors: computed(() => _state.value.errors),
      warnings: computed(() => _state.value.warnings),
      loading: computed(() => _state.value.loading),
    },
  }
}

watch(
  () => _state.value.loading,
  (loading) => {
    if (loading) {
      document.body.classList.add("show-loader")
    } else {
      document.body.classList.remove("show-loader")
    }
  },
)

const clearNotifications = () => {
  _state.value.success = {}
  _state.value.warnings.length = 0
  _state.value.errors.length = 0
}

const setSuccess = (s, skipScroll = false) => {
  if (!skipScroll) {
    window.scrollTo(0, 0)
  }
  if (typeof s === "string") {
    _state.value.success = { message: s }
  } else if (s?.message) {
    _state.value.success = s
  } else {
    setError(new Error("incorrect use of setSuccess"))
    return
  }
}

const setWarning = (s) => {
  if (typeof s === "string") {
    _state.value.warnings.push({ message: s })
  } else if (s?.message) {
    _state.value.warnings.push(s)
  } else {
    _state.value.error = new Error("incorrect use of setWarning")
  }
  _state.value.success = {}
  window.scrollTo(0, 0)
}

const setError = (e) => {
  console.error(e)
  window.scrollTo(0, 0)
  if (e instanceof Error) {
    _state.value.errors.push(e)
  } else if (e?.message) {
    const error = new Error(e.message)
    if (e.stackTrace) {
      error.stackTrace = e.stackTrace
    }
    _state.value.errors.push(error)
  } else {
    _state.value.errors.push(new Error("Got null error."))
  }
  _state.value.success = {}
  _state.value.warnings.length = 0
}

const startLoading = (subsystem) => {
  _state.value.errors.length = 0
  if (!subsystem?.length) {
    throw TypeError("subsystem must be a string")
  }
  _processes.add(subsystem)
  _state.value.loading = _processes.size > 0
  // console.log(`Start:${subsystem} ${_processes.size}`)
}

const stopLoading = (subsystem) => {
  if (!subsystem?.length) {
    throw TypeError("subsystem must be a string")
  }
  _processes.delete(subsystem)
  _state.value.loading = _processes.size > 0
  // console.log(`Stop:${subsystem} ${_processes.size}`)
}

export { useNotification, startLoading, stopLoading, setError, setWarning, setSuccess, clearNotifications }
